// @ts-nocheck

/**
 * MSI3 Enrollment Test Utilities
 * 
 * Provides machine-testable enrollment flows with facility attribution.
 * Simulates the complete patient enrollment lifecycle without Firebase.
 */

import { MSI3_CONSTANTS } from './constants';
import { 
  calculateRevenueSharePercent, 
  calculateMonthlyRevenue,
} from './revenueTestUtils';
import { testDataGenerators } from './patientTestService';

const { REVENUE_STATUS, CITY_DETECTION } = MSI3_CONSTANTS;

// ============================================
// Enrollment Flow Simulation
// ============================================

/**
 * Simulate complete enrollment flow
 * Tests enrollment with/without facility code, city detection, revenue share setup
 */
export const simulateEnrollmentFlow = (options = {}) => {
  const {
    facilityCode = null,
    selectedCityId = null,
    ipCityId = null,
    subscriptionTier = 'free',
    existingEnrollment = null,
    facilityData = null,
    currentPenetration = 0,
  } = options;

  const flowLog = [];
  const result = {
    success: false,
    steps: [],
  };

  // Step 1: Check existing enrollment
  flowLog.push({ step: 'check_existing', data: { hasExisting: !!existingEnrollment } });
  
  if (existingEnrollment) {
    result.error = 'Patient already has an enrollment record';
    result.existingEnrollment = existingEnrollment;
    result.steps = flowLog;
    return result;
  }

  // Step 2: Determine if facility code present
  flowLog.push({ step: 'check_facility_code', data: { hasCode: !!facilityCode } });

  if (!facilityCode) {
    // No facility code - NO revenue share
    flowLog.push({ step: 'create_enrollment', data: { type: 'no_facility' } });
    
    result.success = true;
    result.revenueShare = false;
    result.reason = 'no_facility_code';
    result.enrollment = {
      id: `test-enrollment-${Date.now()}`,
      patientUserId: options.patientUserId || 'test-patient',
      subscriptionTier,
      enrollingFacilityId: null,
      enrollmentCityId: selectedCityId,
      facilityCityId: null,
      cityDetectionMethod: selectedCityId ? CITY_DETECTION.USER_SELECTED : null,
      countsTowardMarketPenetration: !!selectedCityId,
      outOfMarketReason: null,
      marketPenetrationAtEnrollment: null,
      lockedRevenueSharePercentage: 0,
      lockedMonthlyAmount: 0,
      currentRevenueFacilityId: null,
      revenueShareStatus: REVENUE_STATUS.NONE,
      inactivityProtectionMonths: MSI3_CONSTANTS.STANDARD_PROTECTION_MONTHS,
    };
    result.steps = flowLog;
    return result;
  }

  // Step 3: Validate facility code
  flowLog.push({ step: 'validate_facility', data: { code: facilityCode } });
  
  if (!facilityData) {
    result.error = 'Invalid or inactive facility code';
    result.steps = flowLog;
    return result;
  }

  // Step 4: Determine enrollment city (priority: user selected > IP > facility default)
  let enrollmentCityId;
  let cityDetectionMethod;
  
  if (selectedCityId) {
    enrollmentCityId = selectedCityId;
    cityDetectionMethod = CITY_DETECTION.USER_SELECTED;
    flowLog.push({ step: 'city_detection', data: { method: 'user_selected', cityId: enrollmentCityId } });
  } else if (ipCityId) {
    enrollmentCityId = ipCityId;
    cityDetectionMethod = CITY_DETECTION.IP_GEOLOCATION;
    flowLog.push({ step: 'city_detection', data: { method: 'ip_geolocation', cityId: enrollmentCityId } });
  } else {
    enrollmentCityId = facilityData.cityId;
    cityDetectionMethod = CITY_DETECTION.FACILITY_DEFAULT;
    flowLog.push({ step: 'city_detection', data: { method: 'facility_default', cityId: enrollmentCityId } });
  }

  // Step 5: Check in-market status
  const inMarket = enrollmentCityId === facilityData.cityId;
  flowLog.push({ step: 'in_market_check', data: { inMarket, enrollmentCity: enrollmentCityId, facilityCity: facilityData.cityId } });

  const outOfMarketReason = inMarket
    ? null
    : `Enrolled in city ${enrollmentCityId} using facility from city ${facilityData.cityId}`;

  // Step 6: Calculate revenue share
  const revenueSharePct = calculateRevenueSharePercent(currentPenetration);
  const monthlyAmount = calculateMonthlyRevenue(subscriptionTier, revenueSharePct);
  flowLog.push({ 
    step: 'revenue_calculation', 
    data: { 
      penetration: currentPenetration, 
      revenueSharePct, 
      monthlyAmount,
      tier: subscriptionTier,
    } 
  });

  // Step 7: Determine revenue share status
  const revenueStatus = subscriptionTier === 'free' ? REVENUE_STATUS.NONE : REVENUE_STATUS.ACTIVE;
  flowLog.push({ step: 'revenue_status', data: { status: revenueStatus } });

  // Step 8: Create enrollment
  flowLog.push({ step: 'create_enrollment', data: { type: 'with_facility' } });

  result.success = true;
  result.revenueShare = subscriptionTier !== 'free';
  result.facilityId = facilityData.id;
  result.facilityName = facilityData.facilityName;
  result.subscriptionTier = subscriptionTier;
  result.revenueSharePct = revenueSharePct;
  result.monthlyAmount = monthlyAmount;
  result.inMarket = inMarket;
  result.warning = inMarket ? null : 'Out-of-market enrollment flagged';
  result.enrollment = {
    id: `test-enrollment-${Date.now()}`,
    patientUserId: options.patientUserId || 'test-patient',
    subscriptionTier,
    enrollingFacilityId: facilityData.id,
    enrollmentCityId,
    facilityCityId: facilityData.cityId,
    cityDetectionMethod,
    countsTowardMarketPenetration: inMarket,
    outOfMarketReason,
    marketPenetrationAtEnrollment: currentPenetration,
    lockedRevenueSharePercentage: revenueSharePct,
    lockedMonthlyAmount: monthlyAmount,
    currentRevenueFacilityId: subscriptionTier === 'free' ? null : facilityData.id,
    lastClinicalContactDate: new Date(),
    revenueShareStatus: revenueStatus,
    inactivityProtectionMonths: facilityData.earlyAdopterPosition
      ? MSI3_CONSTANTS.EARLY_ADOPTER_PROTECTION_MONTHS
      : MSI3_CONSTANTS.STANDARD_PROTECTION_MONTHS,
  };
  result.steps = flowLog;

  return result;
};

/**
 * Simulate subscription tier change
 * Tests upgrade/downgrade logic and revenue share adjustments
 */
export const simulateTierChange = (options = {}) => {
  const {
    enrollment,
    newTier,
    facilityData = null,
    currentPenetration = 0,
  } = options;

  if (!enrollment) {
    return { success: false, error: 'Patient enrollment not found' };
  }

  const flowLog = [];
  const oldTier = enrollment.subscriptionTier;

  flowLog.push({ step: 'tier_change_start', data: { oldTier, newTier } });

  const updatedEnrollment = { ...enrollment, updatedAt: new Date() };

  // Case 1: Upgrade from Free to Paid
  if (oldTier === 'free' && newTier !== 'free') {
    flowLog.push({ step: 'upgrade_free_to_paid', data: { hasFacility: !!enrollment.enrollingFacilityId } });

    if (enrollment.enrollingFacilityId && facilityData) {
      const revenueSharePct = calculateRevenueSharePercent(currentPenetration);
      const monthlyAmount = calculateMonthlyRevenue(newTier, revenueSharePct);

      flowLog.push({ 
        step: 'calculate_revenue', 
        data: { penetration: currentPenetration, revenueSharePct, monthlyAmount } 
      });

      updatedEnrollment.subscriptionTier = newTier;
      updatedEnrollment.lockedRevenueSharePercentage = revenueSharePct;
      updatedEnrollment.lockedMonthlyAmount = monthlyAmount;
      updatedEnrollment.currentRevenueFacilityId = enrollment.enrollingFacilityId;
      updatedEnrollment.revenueShareStatus = REVENUE_STATUS.ACTIVE;
      updatedEnrollment.lastClinicalContactDate = new Date();
    } else {
      flowLog.push({ step: 'no_revenue_share', data: { reason: 'no_facility' } });

      updatedEnrollment.subscriptionTier = newTier;
      updatedEnrollment.revenueShareStatus = REVENUE_STATUS.NONE;
    }
  }
  // Case 2: Downgrade from Paid to Free
  else if (oldTier !== 'free' && newTier === 'free') {
    flowLog.push({ step: 'downgrade_to_free', data: {} });

    updatedEnrollment.subscriptionTier = newTier;
    updatedEnrollment.revenueShareStatus = REVENUE_STATUS.NONE;
    updatedEnrollment.currentRevenueFacilityId = null;
  }
  // Case 3: Change between paid tiers
  else if (oldTier !== 'free' && newTier !== 'free') {
    const monthlyAmount = calculateMonthlyRevenue(newTier, enrollment.lockedRevenueSharePercentage);
    flowLog.push({ 
      step: 'tier_change_paid', 
      data: { lockedRevenueShare: enrollment.lockedRevenueSharePercentage, monthlyAmount } 
    });

    updatedEnrollment.subscriptionTier = newTier;
    updatedEnrollment.lockedMonthlyAmount = monthlyAmount;
  }
  // Case 4: No change (free to free)
  else {
    flowLog.push({ step: 'no_change', data: {} });
  }

  return {
    success: true,
    oldTier,
    newTier,
    enrollment: updatedEnrollment,
    steps: flowLog,
  };
};

/**
 * Simulate clinical contact and its effects on enrollment
 */
export const simulateClinicalContact = (options = {}) => {
  const {
    enrollment,
    contactFacilityId,
    isOriginalFacility = false,
  } = options;

  if (!enrollment) {
    return { success: false, error: 'Enrollment not found' };
  }

  const flowLog = [];
  const updatedEnrollment = { ...enrollment };

  flowLog.push({ 
    step: 'clinical_contact', 
    data: { 
      facilityId: contactFacilityId, 
      currentStatus: enrollment.revenueShareStatus,
      isOriginalFacility,
    } 
  });

  // Update last contact date
  updatedEnrollment.lastClinicalContactDate = new Date();
  updatedEnrollment.updatedAt = new Date();

  // Check if this restarts paused revenue share
  if (
    enrollment.revenueShareStatus === REVENUE_STATUS.PAUSED &&
    enrollment.subscriptionTier !== 'free' &&
    isOriginalFacility
  ) {
    flowLog.push({ step: 'restart_revenue_share', data: {} });
    updatedEnrollment.revenueShareStatus = REVENUE_STATUS.ACTIVE;
    updatedEnrollment.currentRevenueFacilityId = enrollment.enrollingFacilityId;
  }

  const contact = {
    id: `test-contact-${Date.now()}`,
    patientUserId: enrollment.patientUserId,
    facilityUserId: contactFacilityId,
    contactDate: new Date(),
    contactType: 'chartmind_cmr_scan',
  };

  return {
    success: true,
    contact,
    enrollment: updatedEnrollment,
    revenueShareRestarted: enrollment.revenueShareStatus === REVENUE_STATUS.PAUSED && isOriginalFacility,
    steps: flowLog,
  };
};

/**
 * Simulate protection period expiration check
 */
export const simulateProtectionCheck = (options = {}) => {
  const {
    enrollment,
    recentContacts = [], // Contacts in lookback window
    otherFacilities = [], // Other MSI3 partner facilities
  } = options;

  if (!enrollment) {
    return { success: false, error: 'Enrollment not found' };
  }

  const flowLog = [];
  
  // Calculate if protection has expired
  const lastContact = new Date(enrollment.lastClinicalContactDate);
  const now = new Date();
  const monthsSinceContact = (now - lastContact) / (1000 * 60 * 60 * 24 * 30);
  const protectionExpired = monthsSinceContact >= enrollment.inactivityProtectionMonths;

  flowLog.push({ 
    step: 'protection_check', 
    data: { 
      lastContact: lastContact.toISOString(),
      monthsSinceContact: Math.floor(monthsSinceContact),
      protectionMonths: enrollment.inactivityProtectionMonths,
      expired: protectionExpired,
    } 
  });

  if (!protectionExpired) {
    return {
      success: true,
      action: 'none',
      reason: 'Protection period still active',
      enrollment,
      steps: flowLog,
    };
  }

  // Protection expired - check lookback window
  const lookbackStart = new Date(lastContact);
  lookbackStart.setMonth(lookbackStart.getMonth() + enrollment.inactivityProtectionMonths);
  lookbackStart.setDate(lookbackStart.getDate() - MSI3_CONSTANTS.LOOKBACK_DAYS);

  const otherFacilityContacts = recentContacts.filter(c => 
    c.facilityUserId !== enrollment.currentRevenueFacilityId &&
    otherFacilities.some(f => f.facilityUserId === c.facilityUserId)
  );

  flowLog.push({ 
    step: 'lookback_check', 
    data: { 
      lookbackStart: lookbackStart.toISOString(),
      totalContacts: recentContacts.length,
      otherFacilityContacts: otherFacilityContacts.length,
    } 
  });

  if (otherFacilityContacts.length === 0) {
    // No other facility contacts - pause revenue share
    flowLog.push({ step: 'pause_revenue', data: {} });
    
    const updatedEnrollment = {
      ...enrollment,
      revenueShareStatus: REVENUE_STATUS.PAUSED,
      updatedAt: new Date(),
    };

    return {
      success: true,
      action: 'pause',
      reason: 'No clinical contacts with other MSI3 partners in lookback window',
      enrollment: updatedEnrollment,
      steps: flowLog,
    };
  }

  // Transfer to most recent other facility
  const mostRecentContact = otherFacilityContacts.sort((a, b) => 
    new Date(b.contactDate) - new Date(a.contactDate)
  )[0];

  const newFacility = otherFacilities.find(f => f.facilityUserId === mostRecentContact.facilityUserId);

  flowLog.push({ 
    step: 'transfer_revenue', 
    data: { 
      newFacilityId: newFacility?.id,
      contactDate: mostRecentContact.contactDate,
    } 
  });

  // Note: In real implementation, new revenue share would be calculated at CURRENT penetration
  const updatedEnrollment = {
    ...enrollment,
    currentRevenueFacilityId: newFacility?.id,
    revenueShareStatus: REVENUE_STATUS.ACTIVE,
    lastClinicalContactDate: mostRecentContact.contactDate,
    inactivityProtectionMonths: newFacility?.earlyAdopterProtectionMonths || MSI3_CONSTANTS.STANDARD_PROTECTION_MONTHS,
    updatedAt: new Date(),
  };

  return {
    success: true,
    action: 'transfer',
    reason: 'Revenue share transferred to most recent clinical contact facility',
    newFacilityId: newFacility?.id,
    enrollment: updatedEnrollment,
    steps: flowLog,
  };
};

// ============================================
// Test Scenario Runners
// ============================================

/**
 * Run complete enrollment test scenario
 */
export const runEnrollmentScenario = (scenarioName) => {
  const scenarios = {
    // Basic scenarios
    organic_signup: {
      name: 'Organic Signup (No Facility Code)',
      options: {
        subscriptionTier: 'free',
        selectedCityId: 'nairobi_001',
      },
    },
    
    facility_free: {
      name: 'Facility QR Signup - Free Tier',
      options: {
        facilityCode: 'FAC_001',
        subscriptionTier: 'free',
        facilityData: testDataGenerators.generateFacility('nairobi_001', {
          uniqueSignupCode: 'FAC_001',
          facilityName: 'Test Hospital',
        }),
        currentPenetration: 10,
      },
    },
    
    facility_paid: {
      name: 'Facility QR Signup - Paid Tier',
      options: {
        facilityCode: 'FAC_001',
        subscriptionTier: 'medium',
        facilityData: testDataGenerators.generateFacility('nairobi_001', {
          uniqueSignupCode: 'FAC_001',
          facilityName: 'Test Hospital',
        }),
        currentPenetration: 10,
      },
    },
    
    out_of_market: {
      name: 'Out-of-Market Enrollment',
      options: {
        facilityCode: 'FAC_001',
        subscriptionTier: 'small',
        selectedCityId: 'mombasa_001', // Different city
        facilityData: testDataGenerators.generateFacility('nairobi_001', {
          uniqueSignupCode: 'FAC_001',
          facilityName: 'Nairobi Hospital',
        }),
        currentPenetration: 25,
      },
    },
    
    early_adopter: {
      name: 'Early Adopter Facility Signup',
      options: {
        facilityCode: 'FAC_EA_001',
        subscriptionTier: 'large',
        facilityData: testDataGenerators.generateFacility('nairobi_001', {
          uniqueSignupCode: 'FAC_EA_001',
          facilityName: 'Early Adopter Hospital',
          earlyAdopterPosition: 1,
          earlyAdopterProtectionMonths: 36,
        }),
        currentPenetration: 5,
      },
    },
    
    high_penetration: {
      name: 'High Penetration Market Signup',
      options: {
        facilityCode: 'FAC_002',
        subscriptionTier: 'medium',
        facilityData: testDataGenerators.generateFacility('nairobi_001', {
          uniqueSignupCode: 'FAC_002',
          facilityName: 'High Penetration Hospital',
        }),
        currentPenetration: 70, // 25% revenue share
      },
    },
  };

  const scenario = scenarios[scenarioName];
  if (!scenario) {
    return {
      error: `Unknown scenario: ${scenarioName}`,
      availableScenarios: Object.keys(scenarios),
    };
  }

  const result = simulateEnrollmentFlow(scenario.options);
  
  if (result.revenueShare !== undefined) {
  }
  if (result.monthlyAmount !== undefined) {
  }
  if (result.inMarket !== undefined) {
  }

  return {
    scenario: scenario.name,
    ...result,
  };
};

/**
 * Run all enrollment test scenarios
 */
export const runAllEnrollmentScenarios = () => {
  const scenarioNames = [
    'organic_signup',
    'facility_free',
    'facility_paid',
    'out_of_market',
    'early_adopter',
    'high_penetration',
  ];

  
  const results = scenarioNames.map(name => runEnrollmentScenario(name));
  
  const passed = results.filter(r => r.success).length;
  const failed = results.filter(r => !r.success && !r.error).length;
  const errors = results.filter(r => r.error).length;


  return {
    total: scenarioNames.length,
    passed,
    failed,
    errors,
    results,
  };
};

// ============================================
// Export for window API
// ============================================

if (typeof window !== 'undefined') {
  window.__MSI3_ENROLLMENT_TEST__ = {
    simulateEnrollmentFlow,
    simulateTierChange,
    simulateClinicalContact,
    simulateProtectionCheck,
    runEnrollmentScenario,
    runAllEnrollmentScenarios,
  };
  
}

const enrollmentTestUtilsExports = {
  simulateEnrollmentFlow,
  simulateTierChange,
  simulateClinicalContact,
  simulateProtectionCheck,
  runEnrollmentScenario,
  runAllEnrollmentScenarios,
};

export default enrollmentTestUtilsExports;

